import "keen-slider/keen-slider.min.css";
import React from "react";
import { CasestudyCard, CasestudyCardProps } from ".";
import NewLayout from "@components/new-ui/Layout";
import { CaseStudyHerosection, CaseStudyInformation, FeatureList, Navbar, navLinks, OrganizationProfile } from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const RocaBathroomProduct: React.FC = () => {
    return (
        <NewLayout
            title="Roca Drives Business Growth Through Seamless Customer Journeys"
            description="Find out how Roca improved responsiveness, nurtured leads, and achieved significant growth by consolidating customer communication with Gallabox."
            canonical="https://gallabox.com/case-study/roca-bathroom-product"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/roca-bathroom-product-og.png" }}
        >
            <CaseStudyHerosection
                title="How Roca Bathroom Product Pvt. Ltd. scaled customer engagement with Gallabox"
                description="Find out how Roca improved responsiveness, nurtured leads, and achieved significant growth by consolidating customer communication with Gallabox."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/roca-bathroom-product.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "70%", label: "Improvement in overall business growth" },
                    { percentage: "60%", label: "Improvement in lead generation" },
                    { percentage: "60%", label: "Increase in customer base" },
                ]}
            />
            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Roca Bathroom Product Pvt. Ltd."] },
                                { label: "Industry", value: ["Professional services"] },
                            ]}
                            product={{
                                title: "Features used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "Broadcasts" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "Chatbots" },
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12">
                        <section id="About">
                            <p>
                                Roca Bathroom Product Pvt. Ltd. is a leading brand in the bathroom solutions segment, known for its premium and innovative range of products. With a
                                large customer base and growing demand, Roca wanted to strengthen its customer engagement and streamline communication at scale.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"Challenges before Gallabox"}
                                descriptions={["Before partnering with Gallabox, Roca faced three key challenges:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Missed leads due to slow response times: Roca was generating a steady flow of inquiries, but often struggled to respond instantly. Even a short delay meant that prospects would lose interest or move on to a competitor. Without real-time engagement, valuable leads slipped through the cracks, directly impacting conversion rates and overall sales performance.",
                                    },
                                    {
                                        listItem:
                                            "Wasted time on repetitive queries: A large portion of Roca's customer queries centered on repetitive questions such as product availability, pricing ranges, warranty details, and store locations. Responding to these manually consumed significant time and resources for the sales and support teams. The lack of automation not only slowed down customer response time but also diverted staff from focusing on high-value leads that required personalized attention.",
                                    },
                                    {
                                        listItem:
                                            "Disconnected communication across teams: Roca's communication channels were scattered across different tools and platforms, making it difficult to track, manage, and analyze interactions. Sales, support, and marketing teams often worked in silos, leading to inconsistent responses and missed follow-ups. Without a centralized system, customers experienced delays, and internal teams lacked the visibility needed to collaborate effectively and deliver a seamless customer experience.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <CaseStudyInformation
                                subTitle={"Initial solutions tried"}
                                descriptions={["To overcome their communication challenges, Roca experimented with a mix of tools and processes:"]}
                                className="mt-12"
                            />
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Custom integrations for quick engagement: Roca invested in building custom integrations to speed up responses, but these required ongoing technical effort and constant maintenance. The complexity made it difficult for non-technical team members to adapt quickly, which slowed down the adoption process.",
                                    },
                                    {
                                        listItem:
                                            "Chat automations to handle repetitive queries: Basic chat automation was introduced to answer common customer questions. However, the solutions were limited in scope and failed to cover the wide range of inquiries Roca received daily. This meant that many queries still required manual handling, leaving gaps in customer satisfaction.",
                                    },
                                    {
                                        listItem:
                                            "Broadcasting tools for internal stakeholder communication: To keep internal teams aligned, Roca tried using broadcasting tools to share updates. While this improved reach to some extent, it wasn't integrated with the customer communication system, resulting in fragmented processes and duplicated efforts.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-5">
                                Despite these attempts, the tools were disjointed and lacked scalability. Instead of solving the problem, they created additional layers of
                                complexity, making it harder for teams to collaborate and for customers to get a seamless experience.
                            </p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"Why Roca chose Gallabox"}
                                descriptions={["Roca set out with two primary goals when they chose Gallabox:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    {
                                        listItem:
                                            "Nurture leads more effectively: By engaging prospects instantly and automating repetitive communication, Roca wanted to increase the chances of converting new inquiries into paying customers.",
                                    },
                                    {
                                        listItem:
                                            "Streamline customer communication: The company aimed to unify conversations across channels into one platform, giving teams full visibility and reducing delays in follow-ups.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-5">These goals were central to building a scalable customer engagement model that could support their long-term growth.</p>
                            <p className="mt-5">After evaluating their options, Roca turned to Gallabox because it offered a complete solution under one roof:</p>
                            <FeatureList
                                List={[
                                    { listItem: "A user-friendly interface that allowed teams across sales, marketing, and support to get started without steep learning curves." },
                                    { listItem: "Quick, responsive support that ensured the platform was not just easy to adopt but also reliable during scale." },
                                    { listItem: "A consolidated feature set, from chatbots and broadcasts to integrations, that eliminated the need for juggling multiple tools." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />
                            <p className="mt-5">In short, Gallabox provided the perfect balance of simplicity, functionality, and dependability.</p>
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"Impact on business growth"}
                                descriptions={["The results of implementing Gallabox were immediate and measurable:"]}
                                className="mt-0"
                            />
                            <FeatureList
                                List={[
                                    { listItem: "70% overall improvement in business growth, showcasing Gallabox&apos;s role as a key driver of scale." },
                                    { listItem: "60% increase in lead generation, ensuring that fewer inquiries were lost and more prospects entered the sales pipeline." },
                                    {
                                        listItem:
                                            "50% growth in first-time customer conversion rates, proving that faster and more personalized engagement leads to higher sales success.",
                                    },
                                    { listItem: "60% expansion in the customer base, as streamlined communication built stronger trust and relationships." },
                                    {
                                        listItem:
                                            "10% reduction in marketing and advertising spend, thanks to improved ROI on campaigns driven by smarter automation and better customer targeting.",
                                    },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <p className="mt-5">Gallabox not only solved Roca&apos;s immediate pain points but also set the foundation for sustainable growth.</p>

                            <CaseStudyInformation
                                subTitle={"Elevating everyday interactions into business wins"}
                                descriptions={[
                                    "By leveraging Gallabox, Roca Bathroom Product Pvt. Ltd. has been able to accelerate lead engagement, improve conversion rates, and reduce operational inefficiencies. Gallabox has become a key partner in their growth journey, enabling Roca to scale customer communications with ease.",
                                ]}
                                className="mt-10"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <h3 className="text-gray-900 mb-10 mt-5 text-left ml-4">Check out other customer stories</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto mb-20">
                    {casestudyCardsDetails?.map((data, index) => (
                        <CasestudyCard key={index} {...data} />
                    ))}
                </div>
            </section>
        </NewLayout>
    );
};

export default RocaBathroomProduct;
